import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Button,
  Switch,
  Tooltip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useCurrency } from '../../context/CurrencyContext';
import LocalText from '../common/LocalText';
import { useLocalText } from '../common/useLocalText';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    fontSize: 12,
    fontFamily: 'monospace',
    '& fieldset': { borderColor: '#dde6f0' },
    '&:hover fieldset': { borderColor: '#5383b3' },
    '&.Mui-focused fieldset': { borderColor: '#0a326d', borderWidth: 2 },
  },
};

/**
 * config shape (static UI definition from GATEWAY_CONFIGS):
 *   gatewayKey       string
 *   title            string
 *   subtitle         string
 *   headerBg         string
 *   chipColor        { bg, text, border }
 *   clientIdLabel    string
 *   clientIdIcon     ReactElement
 *   secretKeyLabel   string
 *   secretKeyIcon    ReactElement
 *   icon             ReactElement
 * initialData shape (GatewayConfigResponse from API, or null when not yet configured):
 *   isConfigured        boolean
 *   isEnabled           boolean
 *   maskedClientId      string | null   e.g. "sk_test_**************" — same length
 *                                       as the real value, first 15 chars revealed
 *   maskedSecretKey     string | null   the real value/ciphertext never reaches the browser
 *   supportedCurrencies [{ id, ISO, symbol }]
 *
 * The fields are pre-filled once with the masked value and behave like a
 * completely normal, standard text/password input from then on — select,
 * copy, edit, clear, the eye icon, all work exactly as they would on any
 * other field. Nothing special happens on focus or blur. Saving with a field
 * unchanged from its masked value keeps whatever is already stored
 * server-side; saving with it edited (including cleared to blank) sends the
 * new value. There is nothing to decrypt here — the server never sends
 * anything that could be turned back into the real secret; the "value" is
 * always just the mask, never the real credential.
 */
const GatewayCard = ({ config, initialData, onSave, onToggle, canSave = true, canToggle = true }) => {
  const {
    gatewayKey, title, headerBg,
    clientIdLabel, clientIdIcon, secretKeyLabel, secretKeyIcon,
    icon,
  } = config;

  const { activeCurrency } = useCurrency();

  // Localized placeholder strings used in non-JSX props
  const enterKeyPlaceholder    = useLocalText('Enter key...');
  const enterSecretPlaceholder = useLocalText('Enter secret...');

  // ── Local form state ───────────────────────────────────────────────────────
  const [clientId,  setClientId]  = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [isEnabled, setIsEnabled]   = useState(initialData?.isEnabled ?? false);
  const [saving,   setSaving]   = useState(false);
  const [toggling, setToggling] = useState(false);

  // Tracks the last values that came from the server so we can detect edits.
  const savedRef = useRef({ clientId: '', secretKey: '', isEnabled: false });

  // ── Pre-fill with the masked value whenever initialData changes (on mount +
  // after save/toggle), then behave like a normal field from then on. This is
  // only ever the mask — the real credential is never sent to the browser —
  // so pre-filling it is safe; it's exactly as "sensitive" as showing dashes.
  useEffect(() => {
    const baseEnabled = initialData?.isEnabled ?? false;
    const baseClientId = initialData?.maskedClientId ?? '';
    const baseSecretKey = initialData?.maskedSecretKey ?? '';
    setIsEnabled(baseEnabled);
    setClientId(baseClientId);
    setSecretKey(baseSecretKey);
    savedRef.current = { clientId: baseClientId, secretKey: baseSecretKey, isEnabled: baseEnabled };
  }, [initialData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived state ──────────────────────────────────────────────────────────
  const isConfigured = initialData?.isConfigured
    || (clientId.trim() !== '' && secretKey.trim() !== '');
  const isActive = isConfigured && isEnabled;

  // Blank is only safe when the server already has a value stored to fall
  // back to — otherwise a blank field means "no key was ever set" and blocks Save.
  const hasSavedClientId  = Boolean(initialData?.maskedClientId);
  const hasSavedSecretKey = Boolean(initialData?.maskedSecretKey);
  const isClientIdValid  = clientId.trim()  !== '' || hasSavedClientId;
  const isSecretKeyValid = secretKey.trim() !== '' || hasSavedSecretKey;
  const isValid = isClientIdValid && isSecretKeyValid;

  // Save is enabled only when something actually changed from the last saved state.
  const isDirty =
    clientId  !== savedRef.current.clientId  ||
    secretKey !== savedRef.current.secretKey ||
    (!initialData?.isConfigured && isEnabled !== savedRef.current.isEnabled);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleToggle = async (newValue) => {
    setIsEnabled(newValue);
    if (!initialData?.isConfigured) return;

    setToggling(true);
    try {
      await onToggle(gatewayKey, newValue);
    } catch {
      setIsEnabled(!newValue); // revert on failure
    } finally {
      setToggling(false);
    }
  };

  const handleSave = async () => {
    if (!isDirty || !canSave || !isValid) return;
    setSaving(true);
    try {
      // A field left exactly as its pre-filled mask was never actually edited —
      // send null so the server keeps the real value it already has. Only a
      // field that's actually different (typed over, or cleared) sends its
      // (possibly blank -> null) new value.
      const clientIdChanged  = clientId  !== savedRef.current.clientId;
      const secretKeyChanged = secretKey !== savedRef.current.secretKey;
      await onSave(gatewayKey, {
        clientId:  clientIdChanged  ? (clientId.trim()  || null) : null,
        secretKey: secretKeyChanged ? (secretKey.trim() || null) : null,
        isEnabled,
      });
    } catch {
      // Global api-error handler shows the toast.
    } finally {
      setSaving(false);
    }
  };

  // Build the toggle tooltip text (ReactNode — accepts LocalText)
  const toggleTooltip = toggling
    ? <LocalText>Updating...</LocalText>
    : !initialData?.isConfigured
      ? isEnabled
        ? <LocalText>Will be enabled on save</LocalText>
        : <LocalText>Will be disabled on save</LocalText>
      : isEnabled
        ? <LocalText>Disable gateway</LocalText>
        : <LocalText>Enable gateway</LocalText>;

  return (
    <Box
      sx={{
        bgcolor: 'white',
        borderRadius: 3,
        border: '1px solid rgba(0,0,0,0.06)',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ── Header ──────────────────────────────────────── */}
      <Box sx={{ background: headerBg, p: { xs: 2, sm: 3 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: { xs: 1, sm: 0 } }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: 20 }}>
            {icon}
          </Box>
          <Typography fontWeight={700} fontSize={14} color="white" lineHeight={1.2}>{title}</Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
          <Tooltip title={toggleTooltip}>
            <span>
              <Switch
                checked={isEnabled}
                disabled={toggling || !canToggle}
                onChange={(e) => handleToggle(e.target.checked)}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: 'white' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: 'rgba(255,255,255,0.5)', opacity: 1 },
                  '& .MuiSwitch-track': { bgcolor: 'rgba(255,255,255,0.25)', opacity: 1 },
                  '& .MuiSwitch-thumb': { color: 'white' },
                  '& .MuiSwitch-switchBase.Mui-disabled': { color: 'rgba(255,255,255,0.35)' },
                  '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': { bgcolor: 'rgba(255,255,255,0.12)', opacity: 1 },
                }}
              />
            </span>
          </Tooltip>
          <Box sx={{ px: 1.5, py: 0.5, borderRadius: '999px', bgcolor: isActive ? '#22c55e' : '#94a3b8', fontSize: 10, fontWeight: 800, color: 'white', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            <LocalText>{isActive ? 'Active' : 'Inactive'}</LocalText>
          </Box>
        </Box>
      </Box>

      {/* ── Body ────────────────────────────────────────── */}
      <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* Warning when unconfigured */}
        {!isConfigured && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.5, borderRadius: 2, bgcolor: '#fffbeb', border: '1px solid #fde68a' }}>
            <WarningAmberIcon sx={{ fontSize: 14, color: '#d97706', mt: '1px', flexShrink: 0 }} />
            <Typography fontSize={12} color="#92400e">
              <LocalText>Integration disabled. Configure your keys to activate this gateway.</LocalText>
            </Typography>
          </Box>
        )}

        {/* Default currency info */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.5, borderRadius: 2, bgcolor: '#eff6ff', border: '1px solid #bfdbfe' }}>
          <InfoOutlinedIcon sx={{ fontSize: 14, color: '#2563eb', mt: '1px', flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>
            <Typography fontSize={12} color="#1e3a5f" lineHeight={1.5}>
              <LocalText>Ensure this gateway supports the default currency set for your application.</LocalText>
            </Typography>
            <Box display="flex" alignItems="center" gap={0.75} sx={{ mt: 0.75 }}>
              <Typography fontSize={10} fontWeight={700} sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                <LocalText>Default Currency:</LocalText>
              </Typography>
              {activeCurrency ? (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: '6px', bgcolor: '#dbeafe', border: '1px solid #93c5fd' }}>
                  <Typography fontSize={12} fontWeight={800} sx={{ color: '#1d4ed8' }}>
                    {activeCurrency.symbol}
                  </Typography>
                  <Typography fontSize={12} fontWeight={700} sx={{ color: '#1d4ed8', fontFamily: 'monospace' }}>
                    {activeCurrency.code}
                  </Typography>
                </Box>
              ) : (
                <Typography fontSize={11} sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                  <LocalText>No default currency set</LocalText>
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Client ID field */}
        <Box>
          <Typography fontSize={10} fontWeight={700} sx={{ color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>
            {clientIdLabel}
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            placeholder={enterKeyPlaceholder}
            InputProps={{ startAdornment: <InputAdornment position="start">{clientIdIcon}</InputAdornment> }}
            sx={inputSx}
          />
          {clientId === '' && !hasSavedClientId && (
            <Typography fontSize={10} sx={{ color: '#94a3b8', mt: 0.5 }}>
              <LocalText>Required</LocalText>
            </Typography>
          )}
        </Box>

        {/* Secret Key field */}
        <Box>
          <Typography fontSize={10} fontWeight={700} sx={{ color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>
            {secretKeyLabel}
          </Typography>
          <TextField
            size="small"
            fullWidth
            type={showSecret ? 'text' : 'password'}
            value={secretKey}
            onChange={(e) => setSecretKey(e.target.value)}
            placeholder={enterSecretPlaceholder}
            InputProps={{
              startAdornment: <InputAdornment position="start">{secretKeyIcon}</InputAdornment>,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" edge="end" onClick={() => setShowSecret((v) => !v)} tabIndex={-1}>
                    {showSecret ? <VisibilityOffIcon sx={{ fontSize: 16 }} /> : <VisibilityIcon sx={{ fontSize: 16 }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={inputSx}
          />
          {secretKey === '' && !hasSavedSecretKey && (
            <Typography fontSize={10} sx={{ color: '#94a3b8', mt: 0.5 }}>
              <LocalText>Required</LocalText>
            </Typography>
          )}
        </Box>

      </Box>

      {/* ── Footer / Save ────────────────────────────────── */}
      <Box sx={{ p: 3, pt: 2, borderTop: '1px solid #f1f5f9', bgcolor: '#f8fafc' }}>
        <Button
          fullWidth
          disabled={saving || !isDirty || !canSave || !isValid}
          onClick={handleSave}
          startIcon={saving ? <CircularProgress size={13} sx={{ color: 'inherit' }} /> : <SaveIcon sx={{ fontSize: 15 }} />}
          sx={{
            py: 1.25, borderRadius: '10px', background: headerBg, color: 'white',
            fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '&:hover': { background: headerBg, opacity: 0.88 },
            '&.Mui-disabled': { background: headerBg, color: 'rgba(255,255,255,0.6)', opacity: 0.65 },
          }}
        >
          {saving
            ? <LocalText>Saving...</LocalText>
            : <><LocalText>Save</LocalText> {title.split(' ')[0]} <LocalText>Keys</LocalText></>
          }
        </Button>
      </Box>
    </Box>
  );
};

export default GatewayCard;
